/**
 * The repo list behind the drawer's repo picker.
 *
 * Module-level rather than component-level state: the list is a property of the
 * server, not of the selected task, so switching tasks must not refetch it.
 * Loaded lazily on the first open — a board whose tasks all name their repo
 * already never pays for it. It is per TEAM: a code team sees only its git
 * folders' repos (shared/repoFolders.ts), and Personal every team's the reader is in.
 */
import { ref } from "vue";
import { api } from "./api.js";

export const repos = ref<string[]>([]);
/** Each listed repo's git folder, for the clone line. */
export const repoFolders = ref<Record<string, string>>({});
export const reposError = ref<string | null>(null);
export const reposLoading = ref(false);

/** The team the list is for, once loaded; undefined while nothing is. */
let loadedFor: string | null | undefined;
let inflight: { team: string | null; done: Promise<void> } | null = null;

/** Fetch once per team and cache. `force` re-reads, for the refresh affordance. */
export function loadRepos(team: string | null = null, force = false): Promise<void> {
  if (!force && loadedFor === team) return Promise.resolve();
  if (inflight?.team === team) return inflight.done;

  reposLoading.value = true;
  const done = api
    .get<{ repos: string[]; folders?: Record<string, string> }>(team ? `/api/repos?team=${encodeURIComponent(team)}` : "/api/repos")
    .then(({ repos: list, folders }) => {
      if (inflight?.done !== done) return;
      repos.value = list;
      repoFolders.value = folders ?? {};
      reposError.value = null;
      loadedFor = team;
    })
    .catch((err: unknown) => {
      if (inflight?.done === done) reposError.value = (err as Error).message || "could not load repos";
    })
    .finally(() => {
      if (inflight?.done !== done) return;
      reposLoading.value = false;
      inflight = null;
    });
  inflight = { team, done };
  return done;
}
